import { c as _c } from "react/compiler-runtime";
import { PasswordInput as PasswordInputArk } from '@ark-ui/react/password-input';
import { css, cva } from '@repo/panda/css';
import type React from 'react';
import { Icon } from '../ds/icon';
const indicatorStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 'full'
});
export interface PasswordInputProps {
  errorText?: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'dark' | 'light';
  autoComplete?: React.HTMLInputAutoCompleteAttribute;
  maxLength?: number;
  'aria-label'?: string;
  'aria-describedby'?: string;
  ref?: React.Ref<HTMLInputElement>;
}
const inputStyles = cva({
  base: {
    width: 'full',
    borderRadius: '12',
    border: '[1px solid transparent]',
    transition: '[all 0.2s ease]',
    outline: 'none',
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  variants: {
    variant: {
      dark: {
        backgroundColor: 'whiteAlpha.10',
        color: 'whiteAlpha.80',
        _focus: {
          border: '[1px solid]',
          borderColor: 'whiteAlpha.40',
          backgroundColor: 'whiteAlpha.20'
        },
        _hover: {
          backgroundColor: 'whiteAlpha.20'
        },
        _placeholder: {
          color: 'whiteAlpha.30'
        }
      },
      light: {
        backgroundColor: 'wemod.black.10',
        color: 'wemod.black.80',
        _focus: {
          border: '[1px solid black.40]',
          backgroundColor: 'brand.white',
          boxShadow: '[0 0 0 1px rgba(0, 0, 0, 0.1)]'
        },
        _placeholder: {
          color: 'wemod.black.50'
        }
      }
    },
    size: {
      sm: {
        padding: '[8px 40px 8px 12px]',
        fontSize: 'md'
      },
      md: {
        padding: '[12px 44px 12px 16px]',
        fontSize: 'lg'
      },
      lg: {
        padding: '[16px 48px 16px 20px]',
        fontSize: 'xl'
      },
      xl: {
        padding: '[20px 52px 20px 24px]',
        fontSize: '2xl'
      }
    }
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md'
  }
});
const triggerStyles = cva({
  base: {
    position: 'absolute',
    top: 'none',
    bottom: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'none',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: '[color 0.2s ease]',
    _disabled: {
      cursor: 'not-allowed',
      opacity: 0.5
    }
  },
  variants: {
    variant: {
      dark: {
        color: 'whiteAlpha.40',
        _hover: {
          color: 'whiteAlpha.60'
        }
      },
      light: {
        color: 'wemod.black.60',
        _hover: {
          color: 'wemod.black.80'
        }
      }
    },
    size: {
      sm: {
        right: '8'
      },
      md: {
        right: '12'
      },
      lg: {
        right: '16'
      },
      xl: {
        right: '20'
      }
    }
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md'
  }
});
const errorStyles = cva({
  base: {
    fontSize: 'sm',
    marginTop: '4'
  },
  variants: {
    variant: {
      dark: {
        color: 'red.300'
      },
      light: {
        color: 'red.600'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
export const PasswordInput = t0 => {
  const $ = _c(40);
  const {
    errorText,
    placeholder,
    value,
    onChange,
    disabled: t1,
    fullWidth: t2,
    size: t3,
    variant: t4,
    autoComplete,
    maxLength,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    ref
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const fullWidth = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "md" : t3;
  const variant = t4 === undefined ? "dark" : t4;
  const hasError = Boolean(errorText);
  let t5;
  if ($[0] !== ariaDescribedBy || $[1] !== hasError) {
    t5 = hasError && !ariaDescribedBy ? `password-error-${Math.random().toString(36).substring(2, 9)}` : ariaDescribedBy;
    $[0] = ariaDescribedBy;
    $[1] = hasError;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const errorId = t5;
  const t6 = fullWidth ? "full" : "fit";
  let t7;
  if ($[3] !== t6) {
    t7 = css({
      width: t6
    });
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const rootStyles = t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = css({
      position: "relative",
      display: "flex",
      alignItems: "center"
    });
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const controlStyles = t8;
  let t9;
  if ($[6] !== size || $[7] !== variant) {
    t9 = inputStyles({
      variant,
      size
    });
    $[6] = size;
    $[7] = variant;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = hasError ? errorId : ariaDescribedBy;
  let t11;
  if ($[9] !== ariaLabel || $[10] !== autoComplete || $[11] !== disabled || $[12] !== hasError || $[13] !== maxLength || $[14] !== onChange || $[15] !== placeholder || $[16] !== ref || $[17] !== t10 || $[18] !== t9 || $[19] !== value) {
    t11 = <PasswordInputArk.Input ref={ref} maxLength={maxLength} className={t9} placeholder={placeholder} value={value} onChange={onChange} disabled={disabled} autoComplete={autoComplete} aria-label={ariaLabel} aria-describedby={t10} aria-invalid={hasError} />;
    $[9] = ariaLabel;
    $[10] = autoComplete;
    $[11] = disabled;
    $[12] = hasError;
    $[13] = maxLength;
    $[14] = onChange;
    $[15] = placeholder;
    $[16] = ref;
    $[17] = t10;
    $[18] = t9;
    $[19] = value;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== size || $[22] !== variant) {
    t12 = triggerStyles({
      variant,
      size
    });
    $[21] = size;
    $[22] = variant;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <PasswordInputArk.Indicator className={indicatorStyles} fallback={<Icon icon="visibility_off" size="sm" decorative={true} />}><Icon icon="visibility" size="sm" decorative={true} /></PasswordInputArk.Indicator>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== disabled || $[26] !== t12) {
    t14 = <PasswordInputArk.VisibilityTrigger className={t12} disabled={disabled} aria-label="Toggle password visibility">{t13}</PasswordInputArk.VisibilityTrigger>;
    $[25] = disabled;
    $[26] = t12;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t14) {
    t15 = <PasswordInputArk.Root><PasswordInputArk.Control className={controlStyles}>{t11}{t14}</PasswordInputArk.Control></PasswordInputArk.Root>;
    $[28] = t11;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== errorId || $[32] !== errorText || $[33] !== hasError || $[34] !== variant) {
    t16 = hasError && <div className={errorStyles({
      variant
    })} id={errorId} role="alert" aria-live="polite">{errorText}</div>;
    $[31] = errorId;
    $[32] = errorText;
    $[33] = hasError;
    $[34] = variant;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== rootStyles || $[37] !== t15 || $[38] !== t16) {
    t17 = <div className={rootStyles}>{t15}{t16}</div>;
    $[36] = rootStyles;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};